import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { isInteger } from 'lodash';
import defaultTo from 'lodash/defaultTo';
import { Checkbox } from '@atlaskit/checkbox';
import { ErrorMessage, Field } from '@atlaskit/form';
import Select, { ValueType } from '@atlaskit/select';
import Textfield from '@atlaskit/textfield';
import { Text } from '@atlaskit/primitives';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import { Y200 } from '@atlaskit/theme/colors';
import {
  BillingFrequency,
  EntitlementDetails,
  FetchTransactionAccountForChangeSubscriptionPaneQuery,
  Maybe,
  Offering,
  PricingType,
  SyntheticPricingPlan,
} from '@css-xp/graphtypes/src/generated-types-new';
import { EntitlementBaseProduct } from '@atlassian/cssxp-types-entitlement';
import { OfferingName, PricingPlanType } from '@atlassian/cssxp-typescommon';
import { isStatuspage } from '@atlassian/cssxp-utils-product';
import { EntitlementEdition } from '@atlassian/cssxp-utils-entitlement';
import { isTrialEntitlement } from '@css-xp/entitlements-v2';
import { isOfferingIncluded } from '@css-xp/ppc-utils';
import { ProductOffering } from '@css-xp/graphtypes/src/generated-types-sxp';
import { CurrentPlanSummary } from './current-plan-summary';
import { FormData } from './change-subscription-pane';
import './change-subscription-pane.scss';

interface OfferingSelectItem {
  label: string;
  value: string;
}

interface Props {
  entitlement: EntitlementDetails;
  formState: FormData;
  availableOfferings: Offering[];
  availableInvoiceGroups?: NonNullable<
    FetchTransactionAccountForChangeSubscriptionPaneQuery['transactionAccount']['availableInvoiceGroups']
  >['nodes'];
  isSkipTrialDisabled: boolean;
  isSkipTrialChecked: boolean;
  onSkipTrialChange: (_value: boolean) => void;
  onSkipTrialDisableChange: (_value: boolean) => void;
  productSkipTrialEligible: boolean;
  setProductSkipTrialEligible: (_value: boolean) => void;
  isImmediateFlag: boolean;
  onIsImmediateFlagChange: (_value: boolean) => void;
  evaluatePricingPlan?: (_value: string) => void;
  onSelectOfferingId?: (_value?: string) => void;
  setBillingFrequency: (_value: BillingFrequency) => void;
  billingFrequency: BillingFrequency;
  isBillingCycleChanged?: boolean;
  isCurrentEntitlementFree: boolean | undefined;
  // isDisplayEndNoCostPremiumBanner: (value: boolean) => void;
  isOnUpgradeTrial: boolean;
  isExistingAnnualPlanForJCA?: boolean;
  updateFormState: (_data: FormData) => void;
  showSkipTrialCheckbox?: boolean;
  billToPartyPricingEligibilty?: string | null;
  isCompassPremiumEnabled?: boolean;
  isJPDPremiumOfferingEnabled?: boolean;
  isJPDAsJiraFamilyEnabled?: boolean;
  isDesignIteration1Enabled?: boolean;
  isTWCPhase2Enabled?: boolean;
  isAppEditionSupportEnabled?: boolean;
  linkedSubscriptions: Array<{ id: string; entitlementNumber: string; productId: string; productName: string }>;
}
enum FieldName {
  NEW_PLAN = 'newPlan',
  SKIP_TRIAL = 'skipTrial',
}

export const ManagePlan = ({
  entitlement,
  availableOfferings,
  formState,
  availableInvoiceGroups,
  isSkipTrialChecked,
  isSkipTrialDisabled,
  onSkipTrialChange,
  onSkipTrialDisableChange,
  productSkipTrialEligible,
  setProductSkipTrialEligible,
  onIsImmediateFlagChange,
  evaluatePricingPlan,
  onSelectOfferingId,
  setBillingFrequency,
  billingFrequency,
  isBillingCycleChanged,
  isCurrentEntitlementFree,
  // isDisplayEndNoCostPremiumBanner,
  isOnUpgradeTrial,
  isExistingAnnualPlanForJCA,
  updateFormState,
  showSkipTrialCheckbox = true,
  billToPartyPricingEligibilty,
  isCompassPremiumEnabled,
  isJPDPremiumOfferingEnabled,
  isJPDAsJiraFamilyEnabled,
  isDesignIteration1Enabled,
  isTWCPhase2Enabled,
  isAppEditionSupportEnabled,
  linkedSubscriptions,
}: Props) => {
  const [disableUnits, setDisableUnits] = React.useState<boolean>(true);
  const [plans, setPlans] = useState<ValueType<OfferingSelectItem>>(undefined);
  const shouldResetPricingPlan = useRef(false);
  const initialBillingFrequency = entitlement?.pricingPlan?.billingFrequency;

  const [isOngoingUpgradeTrial, setIsOngoingUpgradeTrial] = useState<boolean>(false);

  const manageUnitField = () => {
    if (
      defaultTo(formState?.newPlan?.label, defaultTo(entitlement?.offering?.name, ''))
        .toLowerCase()
        .includes(OfferingName.AUDIENCE_SPECIFIC.toLowerCase())
    ) {
      setDisableUnits(false);
    } else {
      setDisableUnits(true);
    }
  };

  useEffect(() => {
    setIsOngoingUpgradeTrial(!!entitlement.upgradeTrial);
  }, []);

  React.useEffect(() => {
    manageUnitField();
    setDefaultPricingPlanDetails(entitlement?.offering?.availableSyntheticPricingPlans ?? []);
  }, []);

  const availableActiveOfferingOptions = defaultTo(availableOfferings, [])
    .filter(
      (offering: Offering) =>
        offering.status === 'ACTIVE' &&
        isOfferingIncluded(offering as ProductOffering, {
          isEnterpriseOfferingEnabled: false,
          isJSMRepackagingEnabled: false,
          isCompassPremiumEnabled,
          isJPDPremiumOfferingEnabled,
          isJPDAsJiraFamilyEnabled,
          isAppEditionSupportEnabled,
        })
    )
    .sort((offering1, offering2) => {
      // sort plans in terms of level in asc. order
      if (offering1.level && offering2.level) {
        return offering1.level - offering2.level;
      }
      return 0;
    })
    .map((offering: Offering) => ({
      label: offering.name,
      value: offering.id,
    })) as OfferingSelectItem[];

  const onSelectOffering = (selectedOffering: ValueType<OfferingSelectItem, false>) => {
    onSelectOfferingId && onSelectOfferingId(selectedOffering?.value);
    if (defaultTo(selectedOffering?.label, '').toLowerCase().includes(OfferingName.AUDIENCE_SPECIFIC.toLowerCase())) {
      setDisableUnits(false);
    } else {
      setDisableUnits(true);
    }
    const availableSyntheticPricingPlans =
      availableOfferings?.find((offering: Offering) => offering?.id === selectedOffering?.value)
        ?.availableSyntheticPricingPlans ?? [];
    setDefaultPricingPlanDetails(availableSyntheticPricingPlans);
  };

  const getDefaultBillingGroup = (invoiceGroupId: string) => {
    return availableInvoiceGroups?.filter((invoiceGroup) => {
      return invoiceGroup?.id === invoiceGroupId;
    });
  };

  const scheduledChanges = entitlement?.scheduledChanges ?? null;

  const defaultBillingGroupId = entitlement?.activeSubscriptionBillingGroupId ?? entitlement?.billingGroupId;

  const hasSingleInvoiceGroup = availableInvoiceGroups?.length === 1;

  const scheduledBillingInvoiceGroupId = scheduledChanges?.scheduledBillingInvoiceGroupId;

  const defaultInvoiceGroupPricingPlan = scheduledBillingInvoiceGroupId
    ? getDefaultBillingGroup(scheduledBillingInvoiceGroupId)?.[0]?.shipToPriceEligibility
    : defaultBillingGroupId
    ? getDefaultBillingGroup(defaultBillingGroupId)?.[0]?.shipToPriceEligibility
    : hasSingleInvoiceGroup
    ? availableInvoiceGroups?.[0]?.shipToPriceEligibility
    : undefined;

  const setDefaultPricingPlanDetails = (availableSyntheticPricingPlans: Maybe<Maybe<SyntheticPricingPlan>[]>) => {
    let pricingPlan;

    const availablePricingPlan =
      availableSyntheticPricingPlans?.map((pricingPlan: Maybe<SyntheticPricingPlan>) => pricingPlan?.type) ?? [];

    const txAccountPricingPlan = (billToPartyPricingEligibilty ?? '').toUpperCase();

    const invoiceGroupPricingPlan = (defaultInvoiceGroupPricingPlan ?? '')?.toUpperCase();

    if (
      invoiceGroupPricingPlan &&
      availablePricingPlan.includes(invoiceGroupPricingPlan) &&
      invoiceGroupPricingPlan !== PricingPlanType.COMMERCIAL
    ) {
      pricingPlan = invoiceGroupPricingPlan;
    } else if (
      txAccountPricingPlan &&
      availablePricingPlan.includes(txAccountPricingPlan) &&
      txAccountPricingPlan !== PricingPlanType.COMMERCIAL
    ) {
      pricingPlan = txAccountPricingPlan;
    } else {
      pricingPlan = PricingPlanType.COMMERCIAL;
    }
    evaluatePricingPlan && evaluatePricingPlan(pricingPlan);
  };

  const evaluateOffering = (selectedOffering?: Offering, currentOffering?: Offering) => {
    const isStatuspage = entitlement.baseProduct === EntitlementBaseProduct.STATUS_PAGE;
    const isTrial = isTrialEntitlement(entitlement, isStatuspage);

    if (isStatuspage) {
      setProductSkipTrialEligible(false);
      return;
    }

    if (isBillingCycleChanged && !isCurrentEntitlementFree) {
      onSkipTrialChange(true);
      onSkipTrialDisableChange(true);
      return;
    }

    if (selectedOffering && selectedOffering?.pricingType === PricingType.FREE) {
      setProductSkipTrialEligible(true);
      onSkipTrialChange(true);
      onSkipTrialDisableChange(true);
      return;
    }

    if (!currentOffering?.level || !selectedOffering?.level) return;

    if (currentOffering?.level === selectedOffering?.level) {
      if (!isTrial) {
        setProductSkipTrialEligible(false);
        onSkipTrialChange(false);
        onSkipTrialDisableChange(true);
        return;
      }
      // All trial scenarios now
      // when moving from premium trial to premium paid
      if (selectedOffering?.name.toUpperCase() === EntitlementEdition.PREMIUM && isOngoingUpgradeTrial) {
        setProductSkipTrialEligible(true);
        onSkipTrialChange(true);
        onSkipTrialDisableChange(true);
        // isDisplayEndNoCostPremiumBanner(true);
        return;
      }

      // case when a subscription is moving from trial to paid for same offering name
      // e.g. premium trial => premium paid
      // e.g. standard trial => standard paid
      if (currentOffering.name.toLowerCase() === selectedOffering.name.toLowerCase()) {
        setProductSkipTrialEligible(true);
        onSkipTrialChange(true);
        onSkipTrialDisableChange(true);
        return;
      }
      // for handling the
      setProductSkipTrialEligible(true);
      onSkipTrialChange(false);
      onSkipTrialDisableChange(false);
      return;
    }

    if (currentOffering?.level < selectedOffering?.level) {
      setProductSkipTrialEligible(true);
      // plan Upgrade - not accounting any user/qty change
      onSkipTrialChange(false);
      onSkipTrialDisableChange(false);
    } else {
      setProductSkipTrialEligible(true);
      // plan downgrade - not accounting any user/qty change
      if (isTrial) {
        onSkipTrialChange(false);
        onSkipTrialDisableChange(false);
      } else {
        onSkipTrialChange(true);
        onSkipTrialDisableChange(true);
      }
    }
  };

  const evaluateSkipTrialEligibility = (selectedId: string | undefined) => {
    const selectedOffering = availableOfferings?.find((offering: Offering) => offering?.id === selectedId);
    const offering = entitlement?.offering as Offering;
    evaluateOffering(defaultTo(selectedOffering, undefined), offering);
  };

  const isParentSenAvailable = () => !!entitlement?.parentSen;

  // Return unit count as '1' for FREE SP entitlements , since UTS data cant be relied on in this particular case .
  const statusPageUnitCount = (): string => {
    if (entitlement.offering?.pricingType === PricingType.FREE) {
      return '1';
    } else {
      return defaultTo(entitlement?.unitCount, '1');
    }
  };

  const renderCurrentPlanSummary = () => {
    if (isDesignIteration1Enabled) {
      return (
        <CurrentPlanSummary
          entitlement={entitlement}
          isBillingCycleChanged={isBillingCycleChanged}
          isExistingAnnualPlanForJCA={isExistingAnnualPlanForJCA}
          heading={`Current ${entitlement?.offering?.name} plan`}
          isProductIconRequired
          isDesignIteration1Enabled={isDesignIteration1Enabled}
          isTWCPhase2Enabled={isTWCPhase2Enabled}
          linkedSubscriptions={linkedSubscriptions}
        />
      );
    }
    return (
      <>
        <h2>Current plan</h2>
        <CurrentPlanSummary
          entitlement={entitlement}
          isBillingCycleChanged={isBillingCycleChanged}
          isExistingAnnualPlanForJCA={isExistingAnnualPlanForJCA}
          linkedSubscriptions={linkedSubscriptions}
          isTWCPhase2Enabled={isTWCPhase2Enabled}
        />
      </>
    );
  };

  const getPlanLabel = () => {
    if (isDesignIteration1Enabled) {
      return (
        <Text color="color.text.subtle" size="small" weight="bold">
          Select plan
        </Text>
      );
    }
    return 'Plan';
  };

  const hideEditionChangeOptions = entitlement?.isApp && !isAppEditionSupportEnabled;

  return (
    <div className={classNames('plan-view-section', { 'plan-view-section-new': isDesignIteration1Enabled })}>
      {renderCurrentPlanSummary()}
      {!hideEditionChangeOptions && <h2>New plan</h2>}
      {!isParentSenAvailable() && !hideEditionChangeOptions && (
        <Field<ValueType<OfferingSelectItem>, HTMLSelectElement>
          label={getPlanLabel()}
          name={FieldName.NEW_PLAN}
          defaultValue={defaultTo(plans, formState?.newPlan)}
        >
          {({ fieldProps }) => (
            <div className="plan-select-item" data-testid="new-plan-select">
              <Select<OfferingSelectItem>
                {...fieldProps}
                placeholder={isDesignIteration1Enabled ? 'Select' : undefined}
                isSearchable={false}
                options={availableActiveOfferingOptions}
                value={isBillingCycleChanged && !isCurrentEntitlementFree ? null : defaultTo(plans, formState?.newPlan)}
                className="plan-field"
                billingFrequency
                isDisabled={isExistingAnnualPlanForJCA || (isBillingCycleChanged && !isCurrentEntitlementFree)}
                initialBillingFrequency={initialBillingFrequency}
                onChange={(offering: ValueType<OfferingSelectItem, false>) => {
                  onSelectOffering(offering);
                  evaluateSkipTrialEligibility(offering?.value);
                  shouldResetPricingPlan.current = true;
                  onIsImmediateFlagChange(false);
                  setPlans(offering);
                  if (!isStatuspage(entitlement?.productId)) {
                    if (
                      offering?.label?.toLowerCase().includes('free') &&
                      billingFrequency === BillingFrequency.ANNUAL
                    ) {
                      setBillingFrequency(BillingFrequency.MONTHLY);
                    }
                  }
                  if (offering) {
                    updateFormState({ [FieldName.NEW_PLAN]: offering });
                  }
                  fieldProps.onChange(offering);
                }}
              />
            </div>
          )}
        </Field>
      )}

      {showSkipTrialCheckbox && productSkipTrialEligible && (
        <section>
          {/* Wrapping under the section tag, so the default top margin is not applied */}
          <Field name="skipTrial">
            {({ fieldProps }) => (
              <Checkbox
                {...fieldProps}
                defaultChecked={isSkipTrialChecked}
                isChecked={isSkipTrialChecked || isOngoingUpgradeTrial}
                isDisabled={isSkipTrialDisabled || isOngoingUpgradeTrial}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  onSkipTrialChange(event.currentTarget.checked);
                  fieldProps.onChange(event);
                  updateFormState({ [FieldName.SKIP_TRIAL]: event.currentTarget.checked });
                }}
                testId="skipTrial"
                label="Skip trial"
              />
            )}
          </Field>
        </section>
      )}

      {isOnUpgradeTrial && (
        <span data-testid="upgrade-trial-banner" className="upgrade-trial-banner">
          <WarningIcon label="" primaryColor={Y200} />
          <p>Current plan will be charged during the no-cost trial period of Premium plan</p>
        </span>
      )}

      {isStatuspage(entitlement?.productId) && (
        <Field
          label={'Units'}
          name="units"
          isDisabled={disableUnits}
          defaultValue={defaultTo(formState?.units, statusPageUnitCount())}
          isRequired={true}
          validate={(value: any) =>
            value && value.length > 0 && isInteger(+value) && +value >= 0 ? undefined : 'INVALID'
          }
        >
          {({ fieldProps, error }) => (
            <>
              <Textfield
                className="unit-field"
                {...fieldProps}
                name={'units'}
                testId="units-field"
                data-testid="units-field"
              />
              {error && <ErrorMessage>Enter a valid number</ErrorMessage>}
            </>
          )}
        </Field>
      )}
    </div>
  );
};
